import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, filter, takeUntil } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/core/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ReactivateWithEndDateDialogComponent } from 'src/app/core/components/dialogs/reactivate-with-end-date-dialog/reactivate-with-end-date-dialog.component';
import { SpinnerDialogComponent } from 'src/app/core/components/dialogs/spinner-dialog/spinner-dialog.component';
import { ResponsiveBaseComponent } from 'src/app/core/components/responsive-base/responsive-base.component';
import { MaximumActivePromotionsExceededException } from 'src/app/core/exceptions/maximum-active-promotions-exceeded-exception';
import { NoActiveSubscriptionException } from 'src/app/core/exceptions/no-active-subscription-exception';
import { ValidationException } from 'src/app/core/exceptions/validation-exception';
import { PromotionModel } from 'src/app/models/promotion-model';
import { UpdatePromotionModel } from 'src/app/models/update-promotion-model';
import { SpinnerService } from 'src/app/services/spinner.service';
import { DeletePromotionComponentStore } from 'src/app/stores/components/promotion-delete/delete-promotion.component-store';
import { PromotionDetailComponentStore } from 'src/app/stores/components/promotion-detail/promotion-detail.component-store';
import { UpdatePromotionComponentStore } from 'src/app/stores/components/promotion-update/update-promotion.component-store';
import { selectBusinessAccount } from 'src/app/stores/global/app.selectors';

@Component({
    selector: 'app-promotion-page',
    templateUrl: './promotion-page.component.html',
    styleUrls: ['./promotion-page.component.scss'],
    standalone: false
})
export class PromotionPageComponent extends ResponsiveBaseComponent implements OnInit {
  public viewModel$ = this.promotionDetailComponentStore.viewModel$;
  public editMode: boolean = false;

  public companyId: string;

  private readonly ngUnsubscribeState = new Subject<void>();
  private loadingSpinnerDialogRef: MatDialogRef<SpinnerDialogComponent>;
  private promotion: PromotionModel;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly promotionDetailComponentStore: PromotionDetailComponentStore,
    private readonly dialog: MatDialog,
    private readonly deletePromotionComponentStore: DeletePromotionComponentStore,
    private readonly updatePromotionComponentStore: UpdatePromotionComponentStore,
    private readonly snackBar: MatSnackBar,
    private readonly spinnerService: SpinnerService,
    private readonly store: Store,
    responsive: BreakpointObserver,
  ) { 
    super(responsive); 
  }

  public ngOnInit(): void {
    super.ngOnInit();
    
    this.route.params.subscribe(params => {
      this.store.select(selectBusinessAccount).pipe(filter(result => Boolean(result))).subscribe(businessAccount => {
        const gettingStartedCompanyDetails: boolean = !businessAccount.company.name || businessAccount.company.name === '';
        const gettingStartedCompanyLogo: boolean = !businessAccount.company.logo;
        if (gettingStartedCompanyDetails || gettingStartedCompanyLogo) {
          this.router.navigate(['/getting-started']);
        }
        else {
          this.companyId = params.companyId;
          this.showSpinner();
          this.promotionDetailComponentStore.getPromotion({ companyId: params.companyId, promotionId: params.promotionId });
        }
      });
    });

    this.viewModel$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe(viewModel => {
      this.promotion = viewModel.promotion;
    });

    this.deletePromotionComponentStore.success$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((result) => {
      if (result) {
        this.resetComponentStores();
        this.snackBar.open("Promotion archived successfully", "Dismiss");
        this.router.navigate(['/promotions']);
      }
    });

    this.deletePromotionComponentStore.errorMessage$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((errorMessage) => {
      if (errorMessage !== null && errorMessage !== undefined) {
        this.resetComponentStores();
        if (errorMessage instanceof ValidationException) {
          const validationViolationMessage = errorMessage.violations.map(v => v.message).join("\n");
          this.snackBar.open(validationViolationMessage, "Dismiss");
        }
        else {
          this.snackBar.open(errorMessage, "Dismiss");
        }
      }
    });

    this.updatePromotionComponentStore.success$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((result) => {
      if (result) {
        this.resetComponentStores();
        this.snackBar.open("Promotion updated successfully", "Dismiss");
      }
    });

    this.updatePromotionComponentStore.errorMessage$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((errorMessage) => {
      if (errorMessage !== null && errorMessage !== undefined) {
        this.resetComponentStores();

        let displayErrorMessage = "Something went wrong.";
        if (errorMessage instanceof NoActiveSubscriptionException) {
          displayErrorMessage = 'No active subscriptionplan order. Please buy a subscriptionplan first.';
        }
        else if (errorMessage instanceof MaximumActivePromotionsExceededException) {
          displayErrorMessage = 'You have reached the maximum amount of active promotions with your current subscription. If you want more promotions, consider upgrading your subscriptionplan.';
        }
        if (errorMessage instanceof ValidationException) {
          const validationViolationMessage = errorMessage.violations.map(v => v.message).join("\n");
          this.snackBar.open(validationViolationMessage, "Dismiss");
        }
        else {
          this.snackBar.open(displayErrorMessage, "Dismiss");
        }

        this.snackBar.open(displayErrorMessage, "Dismiss");
      }
    });

    this.updatePromotionComponentStore.promotion$.pipe(takeUntil(this.ngUnsubscribeState), filter(result => Boolean(result))).subscribe((updatedPromotion) => {
      this.promotionDetailComponentStore.setInitial();
      this.promotionDetailComponentStore.getPromotion({ companyId: updatedPromotion.company.id, promotionId: updatedPromotion.id });
    });

    this.promotionDetailComponentStore.loaded$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((loaded) => {
      this.hideSpinner(loaded);
    });

    this.updatePromotionComponentStore.loaded$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((loaded) => {
      this.hideSpinner(loaded);
    });

    this.deletePromotionComponentStore.loaded$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe((loaded) => {
      this.hideSpinner(loaded);
    });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribeState.next();
    this.ngUnsubscribeState.complete();
  }

  public onDelete(event: any): void {
    this.resetComponentStores();

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: "Are you sure?",
        message: "You want to archive this promotion",
        data: this.promotion.name,
        confirm: "Archive"
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.showSpinner();
        this.deletePromotionComponentStore.deleteCampaign({ companyId: this.promotion.company.id, promotionId: this.promotion.id });
      }
    });
  }

  public onActivate(event: any): void {
    this.resetComponentStores();
    this.showSpinner();
    this.updatePromotion(this.promotion.endDate, "ACTIVE");
  }

  public onDeactivate(event: any): void {
    this.resetComponentStores();
    this.showSpinner();
    
    this.updatePromotion(this.promotion.endDate, "INACTIVE");
  }

  public onEditPressed(event: any): void {
    this.editMode = true;
  }

  public onEditCancelPressed(event: any): void {
    this.editMode = false;
  }

  public onUpdateSuccess(event: any): void {
    this.editMode = false;
  }

  private resetComponentStores(): void {
    this.deletePromotionComponentStore.setInitial();
    this.updatePromotionComponentStore.setInitial();
  }

  private showSpinner(): void {
    this.loadingSpinnerDialogRef = this.spinnerService.show();
  }

  private hideSpinner(loaded: boolean): void {
    if (loaded && this.loadingSpinnerDialogRef !== null) {
      this.spinnerService.hide(this.loadingSpinnerDialogRef);
      this.loadingSpinnerDialogRef = null;
    }
  }

  private updatePromotion(promotionEndDate: Date, promotionStatus : string): void {
    const updatePromotion = new UpdatePromotionModel();
    updatePromotion.name = this.promotion.name;
    updatePromotion.description = this.promotion.description;
    updatePromotion.startDate = new Date(this.promotion.startDate);
    updatePromotion.endDate = promotionEndDate;
    updatePromotion.startDateVoucher = new Date(this.promotion.startDateVoucher);
    updatePromotion.endDateVoucher = new Date(this.promotion.endDateVoucher);
    updatePromotion.promotionRewardName = this.promotion.promotionReward.name;
    updatePromotion.promotionRewardDescription = this.promotion.promotionReward.description;
    updatePromotion.recurrencePattern = this.promotion.recurrencePattern ? this.promotion.recurrencePattern : "";
    updatePromotion.promotionStatus = promotionStatus;
    this.updatePromotionComponentStore.updatePromotion({ companyId: this.promotion.company.id, promotionId: this.promotion.id, updatePromotion: updatePromotion });
  }
}
